import { useState } from 'react';
import { Button, Space, message } from 'antd';
import { DeactivateUserModal } from '@viq-admin/ui';
import { deactivateUser, reactivateUser } from '../../api/userDeactivation';
import { fetchAllForExport } from '../../api/leadsExport';
import { useRoleClaim } from '../../auth/useRoleClaim';

interface UserLike {
  oid: string;
  name: string;
  email: string;
  companyId: string;
  companyName?: string;
  deactivatedAt: string | null;
}

// Note: there is deliberately no "Move to company" action. A user's company is
// fixed at invite time — the backend makes companyId immutable on update
// (viq-backend commit 463ae52) to prevent orphaned on-device rows from wedging
// mobile sync. To re-home a user, deactivate and re-invite under the new company.
export function UserActions({
  user,
  onChanged,
}: {
  user: UserLike;
  onChanged: () => void;
}) {
  const { role, companyId: callerCompanyId } = useRoleClaim();
  // A company_admin may only manage users in their own tenant. This predicate
  // preserves that own-company scoping — a plain role check would drop it.
  const canDeactivate =
    role === 'company_admin' && callerCompanyId === user.companyId;

  const [busy, setBusy] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const displayName = user.name || user.email;
  const isDeactivated = user.deactivatedAt != null;

  async function onReactivate() {
    setBusy(true);
    try {
      await reactivateUser(user.oid);
      message.success(`Reactivated ${displayName}.`);
      onChanged();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Reactivate failed');
    } finally {
      setBusy(false);
    }
  }

  if (!canDeactivate) return null;

  return (
    <Space>
      {!isDeactivated && (
        <Button danger onClick={() => setDeactivateOpen(true)}>
          Deactivate user
        </Button>
      )}
      {isDeactivated && (
        <Button onClick={onReactivate} loading={busy}>
          Reactivate user
        </Button>
      )}
      {/* Converged onto the shared modal (already used by the portal users list
          and the console DangerZone) — replaces a hand-rolled Modal.confirm so
          both planes deactivate through one reason-prompting, capture-count UX. */}
      <DeactivateUserModal
        oid={user.oid}
        displayName={displayName}
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onSuccess={onChanged}
        onDeactivate={(oid, reason) => deactivateUser(oid, reason)}
        fetchCaptureCount={(oid) =>
          fetchAllForExport({ repOid: oid }).then((r) => r.rows.length)
        }
      />
    </Space>
  );
}
